import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message, Modal } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Highlighter from "react-highlight-words";
import CSVReader from "react-csv-reader";
import axios from "axios";
import AddSimcard from "../simcardManager/addSimcard";
import EditSimCard from "../simcardManager/editSimcard";
import ViewSimCardDetails from "../simcardManager/viewSimCard";
import "./simcardsV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const simAPI = process.env.REACT_APP_API_SIMCards;
const batchDeleteApi = process.env.REACT_APP_API_BatchDeleteSimCards;
const simBulkUploadEndpoint = process.env.REACT_APP_SIMBulkUploadEndpoint;
const simsSampleFileUrl = process.env.REACT_APP_SIMSBulkUploadSampleFile;

const SimcardsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [simcardData, setSimcardData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedSimId, setSelectedSimId] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewModalVisible, setviewModalVisible] = useState(false);
  const [editModalVisible, setEidtModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchSimcardData();
  }, [open, editModalVisible]);

  const fetchSimcardData = async () => {
    setLoading(true);
    try {
      const res = await fetch(simAPI);
      const data = await res.json();
      const newData = (data.body || []).map((item) => ({
        key: item.id,
        id: item.id,
        country_name: item.country_name,
        mnoName: item.mnoName,
        number: item.number,
        operator: item.operator_name,
        prefix: item.dialing_code,
        msisdn: item.msisdn,
      }));
      setSimcardData(newData);
    } catch (error) {
      console.error("Error fetching sim cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="simcards-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="simcards-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "simcards-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewClick = (id) => {
    setviewModalVisible(true);
    setSelectedSimId(id);
  };

  const handleEditClick = (id) => {
    setEidtModalVisible(true);
    setSelectedSimId(id);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record.key);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${simAPI}/${selectedRecord}`);
      messageApi.success("Sim card deleted successfully");
      setSimcardData((prev) => prev.filter((s) => s.key !== selectedRecord));
    } catch (error) {
      messageApi.error("Failed to delete sim card");
    }
    setDeleteModalVisible(false);
  };

  const handleAddClick = () => setOpen(true);

  const handleDownloadSample = () => {
    if (!simsSampleFileUrl) return;
    const anchor = document.createElement("a");
    anchor.href = simsSampleFileUrl;
    anchor.download = "sample.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleFileUpload = async (data) => {
    if (!simBulkUploadEndpoint) return;
    const formattedData = data.map((item) => ({
      mno_id: item.mno_id,
      msisdn: item.msisdn,
    }));
    try {
      const response = await axios.post(simBulkUploadEndpoint, formattedData);
      if (response.data?.body === "Bulk Records Uploaded") {
        messageApi.success("Records uploaded successfully");
        fetchSimcardData();
      }
    } catch (error) {
      messageApi.error("Error uploading file");
    }
  };

  const columns = [
    {
      title: "#",
      key: "serial",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      width: "18%",
      ...getColumnSearchProps("country_name"),
      sorter: (a, b) => (a.country_name || "").localeCompare(b.country_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Operator",
      dataIndex: "operator",
      key: "operator",
      width: "18%",
      ...getColumnSearchProps("operator"),
      sorter: (a, b) => (a.operator || "").localeCompare(b.operator || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Prefix",
      dataIndex: "prefix",
      key: "prefix",
      width: "10%",
      ...getColumnSearchProps("prefix"),
      sorter: (a, b) => (a.prefix || "").toString().localeCompare((b.prefix || "").toString()),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "MSISDN",
      dataIndex: "msisdn",
      key: "msisdn",
      width: "18%",
      ...getColumnSearchProps("msisdn"),
      sorter: (a, b) => (a.msisdn || "").toString().localeCompare((b.msisdn || "").toString()),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "36%",
      render: (_, record) => (
        <div className="simcards-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewClick(record.key)}
            className="simcards-v2-btn-view"
          >
            View
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record.key)}
            className="simcards-v2-btn-edit"
          >
            Edit
          </Button>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            danger
            className="simcards-v2-btn-delete"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="simcards-v2">
      {contextHolder}
      <div className="simcards-v2-header">
        <h1 className="simcards-v2-title">All Sim Cards</h1>
        <div className="simcards-v2-actions-row">
          <div className="simcards-v2-bulk-actions">
            <CSVReader
              onFileLoaded={handleFileUpload}
              inputId="simcards-csv-input"
              inputStyle={{ display: "none" }}
              parserOptions={{ header: true, skipEmptyLines: true }}
            />
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => document.getElementById("simcards-csv-input")?.click()}
              className="simcards-v2-btn-upload"
            >
              Upload CSV
            </Button>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={handleDownloadSample}
              className="simcards-v2-btn-download"
            >
              Download Sample
            </Button>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddClick}
            className="simcards-v2-add-btn"
          >
            Add Sim Card
          </Button>
        </div>
      </div>

      <div className="simcards-v2-card">
        <Table
          columns={columns}
          dataSource={simcardData}
          loading={loading}
          rowKey="key"
          locale={{ emptyText: loading ? "Loading..." : "No sim cards yet" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} sim cards`,
          }}
          className="simcards-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        className="simcards-v2-delete-modal"
        width={420}
      >
        <p className="simcards-v2-delete-text">
          Are you sure you want to delete this sim card? This action cannot be undone.
        </p>
      </Modal>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="simcards-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AddSimcard setOpen={setOpen} />
      </Dialog>

      <Dialog
        open={viewModalVisible}
        onClose={() => setviewModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="simcards-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <ViewSimCardDetails setOpen={setviewModalVisible} id={selectedSimId} />
      </Dialog>

      <Dialog
        open={editModalVisible}
        onClose={() => setEidtModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="simcards-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <EditSimCard setOpen={setEidtModalVisible} id={selectedSimId} />
      </Dialog>
    </div>
  );
};

export default SimcardsV2;
